/**
 * Lightweight first-run tour over the main modules. Persists completion on
 * the server (profiles.tourSeenVersion) and mirrors prefs.tourSeenVersion
 * locally — no third-party lib. Card docks to alternating left/right side
 * rails so it stays readable without covering the primary content strip.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, m } from 'framer-motion';
import { cn } from '@/lib/utils';
import { Z } from '@/lib/z-layers';
import { usePrefs } from '@/lib/store';
import { trpc } from '@/lib/trpc';
import { consumePostOnboardingRedirect, TOUR_VERSION } from '@/lib/onboarding';
import { Button } from '@/components/Button';
import { EASE } from '@/components/surgery/anim';

const STEPS = [
  {
    id: 'dashboard',
    path: '/',
    titleKey: 'onboarding.tour.dashboardTitle',
    bodyKey: 'onboarding.tour.dashboardBody',
  },
  {
    id: 'library',
    path: '/library',
    titleKey: 'onboarding.tour.libraryTitle',
    bodyKey: 'onboarding.tour.libraryBody',
    placement: 'center',
  },
  {
    id: 'new',
    path: '/new',
    titleKey: 'onboarding.tour.newTitle',
    bodyKey: 'onboarding.tour.newBody',
  },
  {
    id: 'report',
    path: '/report',
    titleKey: 'onboarding.tour.reportTitle',
    bodyKey: 'onboarding.tour.reportBody',
  },
] as const;

export function ModuleTour() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { updatePrefs } = usePrefs();
  const utils = trpc.useUtils();
  const completeTourMut = trpc.profiles.completeTour.useMutation({
    onSuccess: () => {
      void utils.auth.bootstrap.invalidate();
    },
  });
  const [index, setIndex] = useState(0);

  const step = STEPS[index];
  const centered = 'placement' in step && step.placement === 'center';
  /** Alternate rails so each step clears a different side of the viewport. */
  const side: 'left' | 'right' = index % 2 === 0 ? 'right' : 'left';

  // Land on the first tour route when the overlay mounts.
  useEffect(() => {
    navigate(STEPS[0].path, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only
  }, []);
  const isLast = index >= STEPS.length - 1;

  const progressLabel = useMemo(
    () => t('onboarding.tour.progress', { current: index + 1, total: STEPS.length }),
    [t, index],
  );

  const finish = () => {
    // Optimistic local mark so the overlay does not flash back if bootstrap
    // refetch is slow; server write is the durable source of truth.
    updatePrefs({ tourSeenVersion: TOUR_VERSION });
    completeTourMut.mutate({ version: TOUR_VERSION });
    navigate(consumePostOnboardingRedirect(), { replace: true });
  };

  const goNext = () => {
    if (isLast) {
      finish();
      return;
    }
    const next = STEPS[index + 1];
    setIndex(index + 1);
    navigate(next.path);
  };

  const goBack = () => {
    if (index === 0) return;
    const prev = STEPS[index - 1];
    setIndex(index - 1);
    navigate(prev.path);
  };

  return (
    <AnimatePresence mode="wait">
      <m.div
        key={step.id}
        initial={{ opacity: 0, x: centered ? 0 : side === 'right' ? 20 : -20, y: centered ? 12 : 0 }}
        animate={{ opacity: 1, x: 0, y: 0 }}
        exit={{ opacity: 0, x: centered ? 0 : side === 'right' ? 12 : -12, y: centered ? 8 : 0 }}
        transition={{ duration: 0.22, ease: EASE }}
        style={{ zIndex: Z.tour }}
        className={cn(
          'pointer-events-none fixed print:hidden',
          centered
            ? // Middle of the content area, clearing the fixed sidebar.
              'inset-0 flex items-center justify-center px-3 md:pl-[calc(72px+1.5rem)] xl:pl-[calc(248px+2rem)]'
            : cn(
                'top-[calc(var(--shell-header)+0.75rem)]',
                'w-[min(20.5rem,calc(100vw-1.5rem))]',
                // Keep clear of the mobile bottom bar / FAB.
                'max-md:bottom-[calc(6.5rem+env(safe-area-inset-bottom))] max-md:top-auto',
                side === 'right'
                  ? 'right-3 md:right-5 xl:right-8'
                  : // Clear the fixed sidebar (72px md–xl, 248px xl+).
                    'left-3 md:left-[calc(72px+0.75rem)] xl:left-[calc(248px+1rem)]',
              ),
        )}
        role="dialog"
        aria-modal="true"
        aria-labelledby="module-tour-title"
      >
        <div className="pointer-events-auto w-[min(20.5rem,calc(100vw-1.5rem))] rounded-card border border-linea-fuerte bg-papel p-4 shadow-popover">
          <p className="estilo-mono-etiqueta text-tinta-3">{progressLabel}</p>
          <h2 id="module-tour-title" className="mt-1 font-serif text-[20px] font-medium text-tinta">
            {t(step.titleKey)}
          </h2>
          <p className="mt-1.5 text-[13px] leading-5 text-tinta-2">{t(step.bodyKey)}</p>
          <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
            <button
              type="button"
              onClick={finish}
              className="text-[12px] text-tinta-3 underline decoration-linea-fuerte underline-offset-4 hover:text-tinta-2"
            >
              {t('onboarding.tour.skip')}
            </button>
            <div className="flex items-center gap-2">
              {index > 0 && (
                <Button type="button" variant="ghost" className="h-9 px-3" onClick={goBack}>
                  {t('onboarding.tour.back')}
                </Button>
              )}
              <Button type="button" className="h-9 px-4" onClick={goNext}>
                {isLast ? t('onboarding.tour.finish') : t('onboarding.tour.next')}
              </Button>
            </div>
          </div>
        </div>
      </m.div>
    </AnimatePresence>
  );
}
